import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, AlertTriangle, Info, AlertCircle } from "lucide-react";
import { http } from "@/lib/api";

const SEVERITY_ICON = { critical: AlertCircle, warning: AlertTriangle, info: Info };
const SEVERITY_TONE = {
    critical: "text-[hsl(var(--destructive))]",
    warning: "text-amber-500",
    info: "text-brand",
};

/**
 * NotificationBell — Client Portal notification center (mission §76).
 * Backed by GET /organization/notifications, which computes alerts from
 * CURRENT account state (trial ending, payment failed, activation limit,
 * new release, stale invites) rather than a persisted read/unread log —
 * see the backend endpoint's own docstring for why. Polls every 60s,
 * same cadence as PlanLimitBanner.
 */
export default function NotificationBell() {
    const [notifications, setNotifications] = useState([]);
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    const refresh = async () => {
        try {
            const { data } = await http.get("/organization/notifications");
            setNotifications(data.notifications || []);
        } catch {
            // non-critical — fail silently, same as PlanLimitBanner
        }
    };

    useEffect(() => {
        refresh();
        const iv = setInterval(refresh, 60000);
        return () => clearInterval(iv);
    }, []);

    useEffect(() => {
        const onClickOutside = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onClickOutside);
        return () => document.removeEventListener("mousedown", onClickOutside);
    }, []);

    const hasCritical = notifications.some((n) => n.severity === "critical");

    return (
        <div className="relative" ref={ref}>
            <button
                onClick={() => setOpen((v) => !v)}
                data-testid="portal-notification-bell"
                aria-label="Notifications"
                className="mb-focus relative inline-flex h-9 w-9 items-center justify-center rounded-full border border-border bg-background text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
                <Bell size={15} />
                {notifications.length > 0 && (
                    <span
                        data-testid="portal-notification-badge"
                        className={`absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 text-[9px] font-bold text-white ${hasCritical ? "bg-[hsl(var(--destructive))]" : "bg-brand"}`}
                    >
                        {notifications.length}
                    </span>
                )}
            </button>
            {open && (
                <div
                    role="menu"
                    data-testid="portal-notification-panel"
                    className="absolute right-0 top-full z-50 mt-2 w-80 overflow-hidden rounded-lg border border-border bg-surface shadow-2xl"
                >
                    <div className="border-b border-border px-3 py-2.5 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                        Notifications
                    </div>
                    {notifications.length === 0 && (
                        <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                            Nothing needs your attention.
                        </div>
                    )}
                    {notifications.map((n) => {
                        const Icon = SEVERITY_ICON[n.severity] || Info;
                        return (
                            <Link
                                key={n.id}
                                to={n.link || "/portal"}
                                onClick={() => setOpen(false)}
                                data-testid={`portal-notification-${n.id}`}
                                className="flex items-start gap-2.5 border-b border-border px-3 py-3 text-sm last:border-0 hover:bg-muted"
                            >
                                <Icon size={15} className={`mt-0.5 shrink-0 ${SEVERITY_TONE[n.severity] || "text-muted-foreground"}`} />
                                <div className="min-w-0">
                                    <div className="font-medium">{n.title}</div>
                                    <div className="mt-0.5 text-xs text-muted-foreground">{n.message}</div>
                                </div>
                            </Link>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
